/**
 * Wraps a host's raw `onClose` / `onBack` with the record surface's
 * unsaved-changes guard, and hands back the `onGuardChange` callback the
 * `RecordSurfaceProvider` reports the guard through.
 *
 * Every record host (portaled slideout, inline pane) needs the same three
 * things: a guarded close, a guarded back, and to forward the guard upward so
 * the slideout stack can intercept pushes. This is that, once.
 */

import { useCallback, useMemo, useRef } from 'react';

type DismissGuard = (action: () => void) => boolean;

export interface UseGuardedDismissOptions {
  onClose: () => void;
  onBack?: () => void;
  /** Forwarded to the host's own registry (e.g. the stack push guard). */
  onGuardChange?: (guard: DismissGuard | null) => void;
}

export interface GuardedDismiss {
  close: () => void;
  /** `undefined` when the host supplied no `onBack`. */
  back: (() => void) | undefined;
  /** Pass to `RecordSurfaceProvider`'s `onGuardChange`. */
  handleGuardChange: (guard: DismissGuard | null) => void;
}

export function useGuardedDismiss({
  onClose,
  onBack,
  onGuardChange,
}: UseGuardedDismissOptions): GuardedDismiss {
  const guardRef = useRef<DismissGuard | null>(null);

  const handleGuardChange = useCallback(
    (guard: DismissGuard | null) => {
      guardRef.current = guard;
      onGuardChange?.(guard);
    },
    [onGuardChange]
  );

  // The guard is null whenever nothing is pending, so "run through the guard
  // if there is one" is exactly "guard only when there are unsaved changes".
  const runGuarded = useCallback((action: () => void) => {
    if (guardRef.current) {
      guardRef.current(action);
      return;
    }
    action();
  }, []);

  const close = useCallback(() => runGuarded(onClose), [runGuarded, onClose]);
  const back = useMemo(
    () => (onBack ? () => runGuarded(onBack) : undefined),
    [runGuarded, onBack]
  );

  return useMemo(
    () => ({ close, back, handleGuardChange }),
    [close, back, handleGuardChange]
  );
}
